/**
 * SEO path guards — single source of truth for which routes must NOT be indexed.
 *
 * Applied by:
 *  - src/components/seo/DefaultSEO.tsx (global fallback head)
 *  - src/components/seo/SEOHead.tsx    (per-page head)
 *
 * Mirrors the Disallow rules in public/robots.txt so meta robots and robots.txt
 * stay in lockstep. Public marketing/content pages must never appear here.
 */

const UUID_PATTERN =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

// Path prefixes that represent authenticated, internal, or transactional
// surfaces. Crawlers should not index these even if they discover the URL.
const NON_INDEXABLE_PREFIXES: readonly string[] = [
  '/admin',
  '/auth',
  '/api',
  '/dashboard',
  '/onboarding',
  '/profile',
  '/settings',
  '/logout',
  '/unsubscribe',
  '/share/',          // social-prerender redirects, not user content
  '/register',
  '/forgot-password',
  '/reset-password',
  '/user-verification',
  '/calendar',
  '/matches',
  '/demo-booking',
  '/consent-thank-you',
  '/registration-thank-you',
  '/funnel-thank-you',
  '/sponsor-success',
  '/blueprint-success',
  '/gov-no-token',
  '/referral-intake',
];

// Public content routes that legitimately use a UUID in the URL and MUST stay
// indexable (marketing/content pages backed by a database record).
const INDEXABLE_UUID_PREFIXES: readonly string[] = [
  '/resources/case-studies/',
];

export function containsUUID(path: string): boolean {
  return UUID_PATTERN.test(path);
}

export function isNonIndexablePath(path: string): boolean {
  if (!path) return false;
  const normalized = path.startsWith('/') ? path : `/${path}`;
  if (INDEXABLE_UUID_PREFIXES.some((prefix) => normalized.startsWith(prefix))) {
    return false;
  }
  if (containsUUID(normalized)) return true;
  return NON_INDEXABLE_PREFIXES.some(
    (prefix) => normalized === prefix || normalized.startsWith(`${prefix}/`),
  );
}

